import { gradientScaleLinear } from 'utils/colors/colorScale.utils';
import { getContrastRatio, parseHexColor } from 'utils/colors/contrastAudit.utils';

/**
 * Alert cards, forms, and maps all need to interpret severity scores the same way. This file owns
 * the shared color and label rules so those screens do not duplicate severity thresholds.
 */

const alertSeverityColorScale = gradientScaleLinear()
  .domain([0, 66, 100])
  .range(['#34A100', '#EE8918', '#CD2C2E']);

const black = parseHexColor('#000000');
const white = parseHexColor('#ffffff');

/**
 * Returns the theme color associated with an alert severity score.
 *
 * Map polygons, alert cards, and severity controls use this color for a consistent visual scale.
 * Scores move from green through orange to red. Invalid or missing scores use the least-severe
 * green rather than allowing a malformed color value into the UI.
 *
 * @param {number|string|null|undefined} severity - Severity score from 0 through 100.
 * @returns {string} A hexadecimal color value.
 */
export const getAlertSeverityColor = (severity) => {
  const numericSeverity = Number(severity);
  return alertSeverityColorScale(Number.isFinite(numericSeverity) ? numericSeverity : 0);
};

/**
 * Converts an alert severity score into the label displayed throughout the application.
 *
 * Alert detail panels and form controls use these categories when a numeric score would be harder
 * to scan. The thresholds preserve the product's existing Minor, Moderate, Severe, and Extreme
 * scale, while out-of-range or missing values receive an explicit invalid label.
 *
 * @param {number|string|null|undefined} severity - Severity score from 0 through 100.
 * @returns {'Minor'|'Moderate'|'Severe'|'Extreme'|'Invalid value'} The display label.
 */
export const getAlertSeverityLabel = (severity) => {
  if (
    severity === null ||
    severity === undefined ||
    (typeof severity === 'string' && !severity.trim())
  ) {
    return 'Invalid value';
  }

  const numericSeverity = Number(severity);
  if (numericSeverity >= 0 && numericSeverity < 20) return 'Minor';
  if (numericSeverity >= 20 && numericSeverity < 40) return 'Moderate';
  if (numericSeverity >= 40 && numericSeverity < 80) return 'Severe';
  if (numericSeverity >= 80 && numericSeverity <= 100) return 'Extreme';
  return 'Invalid value';
};

/**
 * Returns black or white text, whichever has greater contrast against the severity color.
 *
 * Severity tags use interpolated background colors, so a single foreground color cannot remain
 * readable across the complete scale. Choosing between black and white guarantees an accessible
 * text pairing without changing the established severity palette.
 *
 * @param {number|string|null|undefined} severity - Severity score from 0 through 100.
 * @returns {'#000000'|'#ffffff'} Accessible foreground color for the severity background.
 */
export const getAlertSeverityTextColor = (severity) => {
  const background = parseHexColor(getAlertSeverityColor(severity));
  if (!background) return '#000000';

  return getContrastRatio(black, background) >= getContrastRatio(white, background)
    ? '#000000'
    : '#ffffff';
};

/**
 * Adds the human-readable severity field used by alert presentation components.
 *
 * @param {object} alert - Alert returned by an alert provider.
 * @returns {object} Alert copy with a normalized `severityLabel` field.
 */
export const getAlertWithSeverityLabel = (alert) => ({
  ...alert,
  severityLabel: getAlertSeverityLabel(alert?.severity),
});
